import { EditOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Radio,
  Input,
  Table,
  Spin,
  Space,
  Switch,
  Tabs,
  Tag,
  Breadcrumb,
} from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import useDebounce from "../../../hooks/useDebounce";
import {
  getContactRequest,
  updateContactRequest,
} from "../../../api/requests/contact";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import ContactContactsFilter from "./ContactContactsFilter";
import LogoLoader from "../../../components/common/LogoLoader";
import { DATE_FORMAT } from "../../../utils/date";
import { usePagination } from "../../../hooks/usePagination";
import { useCurrentUser } from "../../../api/hooks/auth";

const { Search } = Input;

function ContactsPage() {
  const filterItems = [
    {
      key: "customers",
      label: "Customers",
      // children: <ContactContactsFilter />,
    },
    {
      key: "employees",
      label: "Employees",
      // children: <ContactContactsFilter />,
    },
    {
      key: "carriers",
      label: "Carriers",
      // children: <ContactContactsFilter />,
    },
  ];

  const navigate = useNavigate();
  const [filterKey, setFilterKey] = useState(filterItems?.[0]?.key);
  const [name, setName] = useState();
  const [type, setType] = useState(["CUSTOMER", "PROCESSOR"]);
  const debouncedName = useDebounce(name, 500);

  const { data: userData } = useCurrentUser();

  const { page, pageSize, onPageChange, onShowSizeChange } = usePagination();

  const handleFilterChange = (key) => {
    setFilterKey(key);
    switch (key) {
      case "customers":
        setType(["CUSTOMER", "PROCESSOR"]);
        break;

      case "employees":
        setType(["ASSISTANT", "OWNER", "ADMIN", "MANAGER"]);
        break;

      case "carriers":
        setType(["CARRIER"]);
        break;
    }
  };

  const params = {
    name: debouncedName,
    type: type,
    page: page,
    limit: pageSize,
  };

  const { data: contactListRes, isLoading: isLoadingContactList } = useQuery({
    queryKey: ["contact", params],
    queryFn: async () => {
      const res = await getContactRequest({
        params,
      });
      return res.data?.data;
    },
  });

  const {
    mutateAsync: updateContact,
    isPending: updatingContact,
    variables,
  } = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await updateContactRequest({
        id,
        data,
      });
      return res.data;
    },
    mutationKey: ["contact/update"],
    onSuccess: (res) => {
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  function navigateToUpdate(id) {
    navigate(`/setup/contacts/update/${id}`);
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => text || "-",
    },
    {
      title: "Business Details",
      dataIndex: "businessDetails",
      key: "businessDetails",
      render: (text) => text ?? "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (text) => text?.join(", ") ?? "-",
    },
    {
      title: "Address",
      key: "address",
      render: ({ address = {} }) => {
        const { street, city } = address;
        return `${street} ${city}`;
      },
    },
    {
      title: "Active",
      key: "active",
      render: ({ active, _id }) => {
        return active ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        );
        // return (
        //   <Switch
        //     loading={updatingContact && variables?.id === _id}
        //     defaultChecked={active}
        //     onChange={(active) => {
        //       updateContact({
        //         id: _id,
        //         data: { active: active },
        //       });
        //     }}
        //   />
        // );
      },
    },
    {
      title: "Username",
      dataIndex: ["systemUser", "username"],
      key: "systemUser.username",
      render: (text) => text ?? "-",
    },
    // {
    //   title: "Updated At",
    //   key: "updatedAt",
    //   render: ({ updatedAt }) => {
    //     return dayjs(updatedAt).format(DATE_FORMAT);
    //   },
    // },
    {
      title: "Action",
      render: (details) => {
        return (
          <Space>
            <Button
              onClick={() => {
                navigateToUpdate(details._id);
              }}
              type="primary"
            >
              <EditOutlined />
            </Button>
          </Space>
        );
      },
      key: "action",
    },
  ];

  function renderTable() {
    if (isLoadingContactList) {
      return <LogoLoader />;
    }

    return (
      <Table
        className="mt-4 overflow-auto px-6"
        dataSource={contactListRes || []}
        columns={columns}
        rowKey={"_id"}
        pagination={{
          total: contactListRes?.count || 0,
          onChange: onPageChange,
        }}
        scroll={{
          x: true,
        }}
      />
    );
  }

  function renderFilterOptions() {
    switch (filterKey) {
      case "customers":
        return (
          <div className="flex items-center gap-4">
            <p className="text-lg">Filters:</p>
            <Radio.Group defaultValue={"ALL"}>
              <Radio.Button
                value={"ALL"}
                onClick={() => setType(["CUSTOMER", "PROCESSOR"])}
              >
                All
              </Radio.Button>
              <Radio.Button
                value={"CUSTOMER"}
                onClick={() => setType(["CUSTOMER"])}
              >
                Customer
              </Radio.Button>
              <Radio.Button
                value={"PROCESSOR"}
                onClick={() => setType(["PROCESSOR"])}
              >
                Processor
              </Radio.Button>
            </Radio.Group>
          </div>
        );

      case "employees":
        return (
          <div className="flex items-center gap-4">
            <p className="text-lg">Filters:</p>
            <Radio.Group>
              <Radio.Button
                value={"ALL"}
                onClick={() =>
                  setType(["OWNER", "ADMIN", "ASSISTANT", "MANAGER"])
                }
              >
                All
              </Radio.Button>
              <Radio.Button value={"OWNER"} onClick={() => setType(["OWNER"])}>
                Owners
              </Radio.Button>
              <Radio.Button value={"ADMIN"} onClick={() => setType(["ADMIN"])}>
                Office-Admins
              </Radio.Button>
              <Radio.Button
                value={"ASSISTANT"}
                onClick={() => setType(["ASSISTANT"])}
              >
                Assistants
              </Radio.Button>
              <Radio.Button
                value={"MANAGER"}
                onClick={() => setType(["MANAGER"])}
              >
                Manager
              </Radio.Button>
            </Radio.Group>
          </div>
        );

      case "carriers":
        return <div></div>;

      default:
        break;
    }
  }

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-6 pb-6">
        <div className="flex flex-col pb-6">
          <div className="flex min-h-14 justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
            <Breadcrumb
              items={[
                {
                  title: <div className="text-lg">Contacts</div>,
                },
              ]}
            />
            <div>
              {(userData?.type?.name === "OWNER" ||
                userData?.type?.name === "ADMIN") && (
                <Button
                  onClick={() => {
                    navigate("/setup/contacts/add");
                  }}
                  type="primary"
                >
                  Create Contact
                </Button>
              )}
            </div>
          </div>
          <div className="mt-6 flex gap-6 px-6">
            <Tabs
              defaultActiveKey={filterItems?.[0]?.key}
              items={filterItems}
              onChange={handleFilterChange}
              className="w-full"
              type="card"
            />
          </div>
          <div className="flex items-center justify-between gap-6 bg-[#FAFAFA] px-6 py-5">
            {renderFilterOptions()}
            <div>
              <Search
                placeholder="Search"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </div>
          </div>
          {renderTable()}
        </div>
      </div>
    </div>
  );
}

export default ContactsPage;
